import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "components/Card";
import InputField from "components/InputField";
import TextField from "components/TextField";
import Button from "components/Button";
import ErrorBox from "components/ErrorBox";
import Backdrop from "components/Backdrop";
import { createClientAPI } from "api/apiHelper";
import { toast } from "react-toastify";

interface ClientFormData {
  name: string;
  email: string;
  phone: string;
  website: string;
  description: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ON_HOLD';
  start_date: string;
  logo: File | null;
}

const CreateClient = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<ClientFormData>({
    name: "",
    email: "",
    phone: "",
    website: "",
    description: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
    status: "ACTIVE",
    start_date: new Date().toISOString().split('T')[0],
    logo: null,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, '');
    setFormData((prev) => ({
      ...prev,
      phone: numericValue,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({
        ...prev,
        logo: e.target.files![0],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      if (formData.email) formDataToSend.append("email", formData.email);
      if (formData.phone) formDataToSend.append("phone", formData.phone);
      if (formData.website) formDataToSend.append("website", formData.website);
      if (formData.description) formDataToSend.append("description", formData.description);
      if (formData.address) formDataToSend.append("address", formData.address);
      if (formData.city) formDataToSend.append("city", formData.city);
      if (formData.state) formDataToSend.append("state", formData.state);
      if (formData.zip_code) formDataToSend.append("zip_code", formData.zip_code);
      if (formData.country) formDataToSend.append("country", formData.country);
      
      formDataToSend.append("status", formData.status);
      formDataToSend.append("start_date", formData.start_date);
      
      if (formData.logo) formDataToSend.append("logo", formData.logo);

      await createClientAPI(formDataToSend);
      toast.success("Client created successfully!");
      navigate("/client-management");
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create client.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Backdrop show={true} />;
  }

  return (
    <div className="mt-5">
      <Card extra="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Create New Client</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Fill in the details below to create a new client account.
          </p>
        </div>

        {error && <ErrorBox errorMessage={error} />}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                id="name"
                name="name"
                label="Client Name *"
                type="text"
                placeholder="Enter client name"
                extra="w-full"
                variant="outlined"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <InputField
                id="email"
                name="email"
                label="Email"
                type="email"
                placeholder="Enter email address"
                extra="w-full"
                variant="outlined"
                value={formData.email}
                onChange={handleChange}
              />
              <InputField
                id="phone"
                name="phone"
                label="Phone"
                type="tel"
                placeholder="Enter phone number"
                extra="w-full"
                variant="outlined"
                value={formData.phone}
                onChange={handlePhoneChange}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <InputField
                id="website"
                name="website"
                label="Website"
                type="url"
                placeholder="Enter website URL"
                extra="w-full"
                variant="outlined"
                value={formData.website}
                onChange={handleChange}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Status *
                    </label>
                    <select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                        <option value="ACTIVE">Active</option>
                        <option value="ON_HOLD">On Hold</option>
                        <option value="INACTIVE">Inactive</option>
                    </select>
                </div>
                
                <InputField
                  id="start_date"
                  name="start_date"
                  label="Relationship Start Date"
                  type="date"
                  extra="w-full"
                  variant="outlined"
                  value={formData.start_date}
                  onChange={handleChange} placeholder={""}                />
            </div>

            <div className="mt-4">
              <TextField
                id="description"
                label="Description"
                placeholder="Enter client description"
                extra="w-full"
                rows={3}
                value={formData.description}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Address Information */}
          <div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Address Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <InputField
                  id="address"
                  name="address"
                  label="Street Address"
                  type="text"
                  placeholder="Enter street address"
                  extra="w-full"
                  variant="outlined"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
              <InputField
                id="city"
                name="city"
                label="City"
                type="text"
                placeholder="Enter city"
                extra="w-full"
                variant="outlined"
                value={formData.city}
                onChange={handleChange}
              />
              <InputField
                id="state"
                name="state"
                label="State/Province"
                type="text"
                placeholder="Enter state or province"
                extra="w-full"
                variant="outlined"
                value={formData.state}
                onChange={handleChange}
              />
              <InputField
                id="zip_code"
                name="zip_code"
                label="Zip/Postal Code"
                type="text"
                placeholder="Enter zip/postal code"
                extra="w-full"
                variant="outlined"
                value={formData.zip_code}
                onChange={handleChange}
              />
              <InputField
                id="country"
                name="country"
                label="Country"
                type="text"
                placeholder="Enter country"
                extra="w-full"
                variant="outlined"
                value={formData.country}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Logo Information */}
          <div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Additional Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Logo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
                />
                {formData.logo && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {formData.logo.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-navy-700">
            <Button
              type="button"
              onClick={() => navigate("/client-management")}
              className="px-6 py-2 border border-gray-300 dark:border-navy-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="px-6 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium dark:bg-brand-400 dark:hover:bg-brand-500"
            >
              Create Client
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateClient;